<!--
  Component: Global help dialog
  Mounted in: App.vue (single global instance)
  Triggered by: useHelpStore().openHelp({ title, body })

  A lightweight, always-closeable popup that explains the current screen.
  The copy itself is owned by each screen (its <i18n> block) and handed to
  the store already translated; this component just renders title + body
  sections (optional bold sub-heading + paragraph). Mirrors the
  PurchaseDialog → entitlement store pattern.
-->
<template>
  <Dialog v-model:show="help.open" variant="small" :blur="true" :closeable="true">
    <h1>{{ help.title }}</h1>
    <template v-for="(section, i) in help.body" :key="i">
      <h2 v-if="section.heading" class="help-heading">{{ section.heading }}</h2>
      <p class="help-text">{{ section.text }}</p>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import Dialog from '@/components/Dialog.vue'
import { useHelpStore } from '@/stores/help'

const help = useHelpStore()
</script>

<style scoped lang="css">
/* Bold sub-heading for grouped help copy. Dialog styles :slotted(h1)/(p)
   but has no rule for h2, so size it here. Tight bottom margin pulls its
   paragraph up underneath it as one visual block. */
h2.help-heading {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  margin: 1.25rem 0 0.35rem 0;
}

/* Honour `\n` line breaks in the copy (e.g. the "gleich → … / randomisiert
   → …" option explanations) and dial the body size down from Dialog's
   default 1.2rem so multi-section help reads comfortably. */
p.help-text {
  white-space: pre-line;
  font-size: 1rem !important;
  line-height: 1.5;
  margin-bottom: 0.75rem;
}
</style>
